import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import { CategoryRow } from "../components/CategoryRow";
import { StreamGrid } from "../components/StreamGrid";
import { EmptyState } from "../components/EmptyState";
import { useCatalog } from "../contexts/CatalogContext";
import { useScrollRestoration } from "../lib/scrollRestoration";
import type { Category, Stream } from "../lib/types";

interface Props {
  title: string;
  pick: (c: NonNullable<ReturnType<typeof useCatalog>["catalog"]>) => {
    streams: Stream[];
    categories: Category[];
  };
  layout?: "landscape" | "portrait";
  hrefFor?: (s: Stream) => string;
  emptySuggestSetup?: boolean;
}

export default function CategoryPage({
  title,
  pick,
  layout = "portrait",
  hrefFor,
  emptySuggestSetup = true,
}: Props) {
  const { catalog, activePlaylist, error, loading } = useCatalog();
  // Active category lives in ?cat=... so back-navigation from /watch lands the
  // user on the same filtered view they came from. "All" is the default and
  // omitted from the URL to keep things clean.
  const [searchParams, setSearchParams] = useSearchParams();
  const active = searchParams.get("cat") || "All";
  const setActive = (next: string) => {
    setSearchParams(
      (prev) => {
        const sp = new URLSearchParams(prev);
        if (!next || next === "All") sp.delete("cat");
        else sp.set("cat", next);
        return sp;
      },
      { replace: true },
    );
  };

  const data = useMemo(() => {
    if (!catalog) return null;
    return pick(catalog);
  }, [catalog, pick]);

  useScrollRestoration(!!data);

  if (!activePlaylist) {
    return (
      <PageLayout>
        <EmptyState
          title="Pick a playlist"
          hint="Add an Xtream or M3U playlist in Settings, then come back here."
          cta={emptySuggestSetup ? { to: "/settings", label: "Open settings" } : undefined}
        />
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout>
        <EmptyState title="Couldn't load catalog" hint={error} cta={{ to: "/settings", label: "Check playlist" }} />
      </PageLayout>
    );
  }

  if (!data && loading) {
    return (
      <PageLayout>
        <div className="px-4 md:px-8 py-6">
          <PageHeader title={title} />
          <SkeletonGrid />
        </div>
      </PageLayout>
    );
  }

  if (!data) {
    return (
      <PageLayout>
        <EmptyState title="No data yet" hint="Try refreshing the catalog." />
      </PageLayout>
    );
  }

  const filtered = active === "All" ? data.streams : data.streams.filter((s) => s.category_id === active);

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4">
        <PageHeader title={title} count={data.streams.length} />
        <div className="my-4">
          <CategoryRow categories={data.categories} active={active} onChange={setActive} />
        </div>
        <StreamGrid streams={filtered} layout={layout} hrefFor={hrefFor} />
      </div>
    </PageLayout>
  );
}

function PageHeader({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-end justify-between gap-4">
      <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary">
        {title}
      </h1>
      {typeof count === "number" && (
        <span className="text-[11px] font-mono uppercase tracking-[0.04em] text-text-secondary mb-2">
          {count.toLocaleString()} total
        </span>
      )}
    </div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-3 mt-4">
      {Array.from({ length: 24 }).map((_, i) => (
        <div key={i} className="aspect-[2/3] rounded-lg skeleton-shimmer" />
      ))}
    </div>
  );
}
